/**
 * B2B Matrix Grid — assets/b2b-matrix-grid.js
 * Colour-aware version with Split-Pairing Network Payload Interceptor
 * Includes Dealeasy Math, Theme State Pacifier, Visual Text Formatter, and UI Cleanup
 */

(function () {
  'use strict';

  // ─── Money formatting ─────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function sanitiseQty(input) {
    let val = parseInt(input.value, 10);
    if (isNaN(val) || val < 0) val = 0;
    const max = parseInt(input.getAttribute('max'), 10);
    if (!isNaN(max) && val > max) val = max;
    input.value = val;
    return val;
  }

  function collectProperties(form) {
    const textProps = {};
    const inputs = form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]');
    inputs.forEach(function (el) {
      const key = el.name.replace(/^properties\[/, '').replace(/\]$/, '');
      if (el.type === 'checkbox' && !el.checked) return;
      if (el.value !== '') textProps[key] = el.value;
    });
    return textProps;
  }

  // ─── Per-matrix init ──────────────────────────────────────────────────────
  function initMatrix(wrapper) {
    const form = wrapper.closest('form[action*="/cart/add"]');
    if (!form) return;

    const dataEl = wrapper.querySelector('.b2b-variant-data');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[B2B Matrix] Could not parse variant data', e); return; }

    const colourIndex = parseInt(wrapper.getAttribute('data-colour-index'), 10) || 0;
    const sizeIndex   = parseInt(wrapper.getAttribute('data-size-index'),   10) || 1;

    const optionKeys = ['option1', 'option2', 'option3'];
    function getColour(v) { return v[optionKeys[colourIndex]] || ''; }
    function getSize(v)   { return v[optionKeys[sizeIndex]]   || ''; }

    const colourMap = {};
    variants.forEach(function (v) {
      const c = getColour(v);
      if (!colourMap[c]) colourMap[c] = [];
      colourMap[c].push(v);
    });

    const allSizes = [];
    variants.forEach(function (v) {
      const s = getSize(v);
      if (s && !allSizes.includes(s)) allSizes.push(s);
    });

    const blocksContainer = wrapper.querySelector('[data-colour-blocks]');
    const prompt          = wrapper.querySelector('[data-b2b-prompt]');
    const grandFooter     = wrapper.querySelector('[data-grand-footer]');
    const totalUnitsEl    = wrapper.querySelector('[data-total-units]');
    const grandTotalEl    = wrapper.querySelector('[data-grand-total]');
    const validMsg        = wrapper.querySelector('[data-b2b-validation]');
    const stepLabel       = wrapper.querySelector('.b2b-step-label');
    
    // Dynamically re-style and hide the Step Label
    if (stepLabel) {
       stepLabel.innerHTML = 'Sizes';
       stepLabel.style.fontWeight = '400';
       stepLabel.style.display = 'none';
    }

    const activeBlocks = {};
    let tiers = [];

    // ── THEME PACIFIER ── 
    function forceThemeSync(variant) {
      if (!variant) return;
      
      const idInputs = form.querySelectorAll('select[name="id"], input[name="id"]');
      idInputs.forEach(function(el) {
          el.value = variant.id;
          el.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      const opts = [variant.option1, variant.option2, variant.option3];
      opts.forEach(function(optVal) {
          if (!optVal) return;
          form.querySelectorAll('input[type="radio"]').forEach(function(radio) {
              if (radio.value === optVal || radio.getAttribute('data-value') === optVal) {
                  radio.checked = true;
                  radio.dispatchEvent(new Event('change', { bubbles: true }));
              }
          });
          form.querySelectorAll('select:not([name="id"])').forEach(function(sel) {
              for (let i = 0; i < sel.options.length; i++) {
                  if (sel.options[i].value === optVal) {
                      sel.value = optVal;
                      sel.dispatchEvent(new Event('change', { bubbles: true }));
                      break;
                  }
              }
          });
      });
    }

    // ── DEALEASY VISUAL FORMATTER ──
    function formatDealeasyWidget() {
       const table = document.querySelector('.lb-vd-table');
       if (!table || table.dataset.formatted) return;

       document.querySelectorAll('.lb-vd-table-body tr').forEach(function(row) {
          const cells = row.querySelectorAll('td');
          if (cells.length < 2) return;
          cells[0].textContent = cells[0].textContent.replace(/Buy\s*/i, '');
       });

       table.dataset.formatted = "true";
       tiers = scrapeDealeasyTiers();
       recalcGrand();
    }
    
    const dealeasyObserver = new MutationObserver(formatDealeasyWidget);
    dealeasyObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(formatDealeasyWidget, 1000);

    // ── DEALEASY MATH SCRAPER ──
    function scrapeDealeasyTiers() {
      const result = [];
      document.querySelectorAll('.lb-vd-table-body tr').forEach(function (row) {
        const cells = row.querySelectorAll('td');
        if (cells.length < 2) return;
        
        let qtyText = cells[0].textContent.trim();
        let priceText = cells[1].textContent.trim();
        
        if (qtyText.includes('£') || qtyText.includes('%')) {
            let temp = qtyText;
            qtyText = priceText;
            priceText = temp;
        }

        const qtyMatch = qtyText.match(/(\d+)/);
        const minQty = qtyMatch ? parseInt(qtyMatch[1], 10) : null;

        let price = 0;
        let isDiscount = false;
        let discountPct = 0;

        if (priceText.includes('%')) {
           isDiscount = true;
           const pctMatch = priceText.match(/(\d+)/);
           if (pctMatch) discountPct = parseInt(pctMatch[1], 10);
        } else {
           const cleanPrice = priceText.replace(/[^0-9.]/g, '');
           price = Math.round(parseFloat(cleanPrice) * 100);
        }

        if (minQty !== null) {
          result.push({ minQty: minQty, pricePerUnit: price, isDiscount: isDiscount, discountPct: discountPct });
        }
      });
      
      result.sort(function (a, b) { return b.minQty - a.minQty; });
      return result;
    }
    
    function getTierPrice(totalUnits, basePrice) {
      if (!tiers.length) return basePrice;
      for (let i = 0; i < tiers.length; i++) {
        if (totalUnits >= tiers[i].minQty) {
           if (tiers[i].isDiscount) {
              return Math.round(basePrice * (1 - (tiers[i].discountPct / 100)));
           }
           return tiers[i].pricePerUnit;
        }
      }
      return basePrice;
    }

    tiers = scrapeDealeasyTiers();
    if (!tiers.length) setTimeout(function () { tiers = scrapeDealeasyTiers(); }, 1500);

    function recalcGrand() {
      let totalUnits = 0;
      wrapper.querySelectorAll('[data-row-input]').forEach(function (input) {
        totalUnits += sanitiseQty(input);
      });

      const masterQtyInputs = form.querySelectorAll('input[name="quantity"]');
      if (masterQtyInputs.length > 0) {
        masterQtyInputs.forEach(inp => {
          inp.value = totalUnits > 0 ? totalUnits : 1;
          inp.dispatchEvent(new Event('input', { bubbles: true }));
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      const firstInput = wrapper.querySelector('[data-row-input]');
      const basePrice  = firstInput ? (parseInt(firstInput.getAttribute('data-price'), 10) || 0) : 0;
      const tierPrice  = getTierPrice(totalUnits, basePrice);
      const totalCents = tierPrice * totalUnits;

      Object.keys(activeBlocks).forEach(function (colourName) {
        const block = activeBlocks[colourName];
        let colourUnits = 0;
        block.querySelectorAll('[data-row-input]').forEach(function (input) {
          colourUnits += parseInt(input.value, 10) || 0;
        });
        const colourCents = tierPrice * colourUnits;
        const unitsEl    = block.querySelector('[data-colour-units]');
        const subtotalEl = block.querySelector('[data-colour-subtotal]');
        if (unitsEl)    unitsEl.textContent    = colourUnits;
        if (subtotalEl) subtotalEl.textContent = formatMoney(colourCents);
      });

      if (totalUnitsEl) totalUnitsEl.textContent = totalUnits;
      if (grandTotalEl) grandTotalEl.textContent = formatMoney(totalCents);
      if (grandFooter)  grandFooter.style.display = totalUnits > 0 ? 'flex' : 'none';
    }

    function buildColourBlock(colourName, swatchEl) {
      const colourVariants = colourMap[colourName] || [];
      if (!colourVariants.length) return null;

      let swatchBg = '#ccc';
      let swatchImg = null;
      if (swatchEl) {
        const label = swatchEl.querySelector('label');
        const target = label || swatchEl;
        const bgImage = target.style.backgroundImage;
        const bgColor = target.style.backgroundColor;
        if (bgImage && bgImage !== 'none' && bgImage !== '') {
          swatchImg = bgImage.replace(/^url\(["']?/, '').replace(/["']?\)$/, '');
          swatchBg = bgColor || '#ccc';
        } else if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
          swatchBg = bgColor;
        }
      }

      const block = document.createElement('div');
      block.className = 'b2b-colour-block';
      block.setAttribute('data-colour-block', colourName);

      const header = document.createElement('div');
      header.className = 'b2b-colour-header';
      const dotStyle = swatchImg ? `background-image:url('${swatchImg}');background-size:cover;background-color:${swatchBg};` : `background:${swatchBg};`;
      header.innerHTML = `
        <span class="b2b-colour-swatch" style="${dotStyle}"></span>
        <span class="b2b-colour-name">${colourName}</span>
        <button type="button" class="b2b-colour-remove" aria-label="Remove ${colourName}">&#x2715;</button>
      `;
      block.appendChild(header);

      header.querySelector('.b2b-colour-remove').addEventListener('click', function () {
        deselectColour(colourName);
      });

      const grid = document.createElement('div');
      grid.className = 'b2b-size-grid';
      const inner = document.createElement('div');
      inner.className = 'b2b-size-grid-inner';

      allSizes.forEach(function (size) {
        const variant = colourVariants.find(function (v) { return getSize(v) === size; });
        if (!variant) return; 

        const cell = document.createElement('div');
        cell.className = 'b2b-size-cell' + (variant.available ? '' : ' b2b-size-cell--oos');
        cell.setAttribute('data-size-cell', variant.id);
        cell.innerHTML = `
          <span class="b2b-size-label">${size}</span>
          <span class="b2b-size-price">${formatMoney(variant.price)}</span>
          ${variant.available ? `
            <div class="b2b-qty-stepper">
              <button type="button" class="b2b-qty-btn b2b-qty-plus" aria-label="Increase ${size}">+</button>
              <input type="number" class="b2b-matrix-input" data-row-input data-variant-id="${variant.id}" data-price="${variant.price}" value="0" min="0" max="${variant.inventory_quantity || 9999}" inputmode="numeric" pattern="[0-9]*">
              <button type="button" class="b2b-qty-btn b2b-qty-minus" aria-label="Decrease ${size}">−</button>
            </div>
          ` : `
            <div class="b2b-qty-stepper" style="opacity:0.4;">
              <span style="padding:8px;font-size:0.75rem;color:#999;text-align:center;">Out of<br>stock</span>
            </div>
          `}
        `;

        if (variant.available) {
          const input   = cell.querySelector('[data-row-input]');
          const plusBtn = cell.querySelector('.b2b-qty-plus');
          const minBtn  = cell.querySelector('.b2b-qty-minus');

          plusBtn.addEventListener('click', function () {
            let v = parseInt(input.value, 10) || 0;
            const max = parseInt(input.getAttribute('max'), 10);
            if (!isNaN(max) && v >= max) return;
            input.value = v + 1;
            updateCell(cell, input); recalcGrand();
          });
          minBtn.addEventListener('click', function () {
            let v = parseInt(input.value, 10) || 0;
            if (v > 0) input.value = v - 1;
            updateCell(cell, input); recalcGrand();
          });
          input.addEventListener('input', function () {
            sanitiseQty(input); updateCell(cell, input); recalcGrand();
          });
        }
        inner.appendChild(cell);
      });

      grid.appendChild(inner);
      block.appendChild(grid);
      const totalRow = document.createElement('div');
      totalRow.className = 'b2b-colour-total-row';
      totalRow.innerHTML = `<span>Units: <strong data-colour-units>0</strong></span><span>Subtotal: <strong data-colour-subtotal>${formatMoney(0)}</strong></span>`;
      block.appendChild(totalRow);

      return block;
    }

    function updateCell(cell, input) {
      const qty = parseInt(input.value, 10) || 0;
      cell.classList.toggle('b2b-size-cell--active', qty > 0);
      if (qty > 0) {
        const vId = parseInt(input.getAttribute('data-variant-id'), 10);
        const variant = variants.find(function(v) { return v.id === vId; });
        if (variant) forceThemeSync(variant);
      }
    }

    function selectColour(colourName, swatchEl) {
      if (activeBlocks[colourName]) return; 
      const colourVariants = colourMap[colourName] || [];
      const targetVariant  = colourVariants.find(function(v) { return v.available; }) || colourVariants[0];
      
      if (targetVariant && targetVariant.available) {
        forceThemeSync(targetVariant);
      }

      const block = buildColourBlock(colourName, swatchEl);
      if (!block) return;
      
      if (prompt) prompt.style.display = 'none';
      if (stepLabel) stepLabel.style.display = 'block'; // Reveal the Sizes title
      
      blocksContainer.appendChild(block);
      activeBlocks[colourName] = block;
      recalcGrand();
    }

    function deselectColour(colourName) {
      const block = activeBlocks[colourName];
      if (!block) return;
      block.querySelectorAll('[data-row-input]').forEach(function (input) { input.value = 0; });
      block.remove();
      delete activeBlocks[colourName];

      if (Object.keys(activeBlocks).length === 0) {
        if (prompt) prompt.style.display = '';
        if (stepLabel) stepLabel.style.display = 'none'; // Hide Sizes title
        if (grandFooter) grandFooter.style.display = 'none';
      }
      recalcGrand();
    }

    // ── NUKES NATIVE SIZE & QUANTITY TEXT ──
    function hideSizeButtons() {
      const byIndex = document.querySelectorAll(`[data-option-index="${sizeIndex}"], [data-index="option${sizeIndex + 1}"]`);
      byIndex.forEach(function (el) { el.style.display = 'none'; });
      
      // Aggressively hunt down any label or tag outside the matrix containing exactly the word "Size" or "Quantity"
      document.querySelectorAll('label, legend, .label, .swatch-title, .option-name, .swatch-option-title, span').forEach(function(el) {
          if (el.closest('.b2b-matrix-wrapper')) return;
          const txt = el.textContent.trim().toLowerCase();
          
          if (txt === 'size' || txt === 'quantity') {
              el.style.display = 'none';
              
              // Hide the quantity input wrapper next to the label
              if (txt === 'quantity' && el.nextElementSibling) {
                  el.nextElementSibling.style.display = 'none';
              }
              
              // If it's wrapped in a div, kill the wrapper div too
              if (el.parentElement && el.parentElement.tagName === 'DIV' && el.parentElement.children.length <= 2) {
                  el.parentElement.style.display = 'none';
              }
          }
      });
      
      document.querySelectorAll('.swatch-wrapper, .product-form-option, .selector-wrapper, [data-option-name]').forEach(function (el) {
        const name = (el.getAttribute('data-option-name') || el.querySelector('label, .label, .option-name')?.textContent || '').toLowerCase().trim();
        if (name === 'size') el.style.display = 'none';
      });
    }

    hideSizeButtons();
    setTimeout(hideSizeButtons, 500);

    function isColourSwatch(el) {
      if (!el.matches('.swatch-element')) return false;
      const swatchGroup = el.closest('.swatch[data-option-index]');
      if (swatchGroup && parseInt(swatchGroup.getAttribute('data-option-index'), 10) === sizeIndex) return false;
      const fieldset = el.closest('fieldset');
      if (fieldset) {
        const legend = fieldset.querySelector('legend, .swatch-title');
        if (legend && legend.textContent.trim().toLowerCase() === 'size') return false;
      }
      return true;
    }

    document.addEventListener('click', function (e) {
      const swatch = e.target.closest('.swatch-element');
      if (!swatch || !isColourSwatch(swatch)) return;
      const productSection = wrapper.closest('[data-section-id], #product-box, .entry-content');
      if (productSection && !productSection.contains(swatch)) return;

      const colourName = swatch.getAttribute('data-value') || swatch.getAttribute('data-option-value') || swatch.getAttribute('data-colour') || swatch.textContent.trim();
      if (!colourName) return;

      if (activeBlocks[colourName]) {
        deselectColour(colourName);
        swatch.classList.remove('b2b-swatch-selected');
      } else {
        selectColour(colourName, swatch);
        swatch.classList.add('b2b-swatch-selected');
      }
    });

    // ── B2B Network Payload Expander (Split-Pairing Engine) ──────────────────────
    if (!window._b2bNetworkInstalled) {
      window._b2bNetworkInstalled = true;

      function getMatrixItems() {
        const activeWrapper = document.querySelector('[data-b2b-matrix]');
        if (!activeWrapper) return null;
        
        const itemsToAdd = [];
        let totalQty = 0;
        activeWrapper.querySelectorAll('[data-row-input]').forEach(function (input) {
          const qty = parseInt(input.value, 10) || 0;
          if (qty > 0) {
            itemsToAdd.push({ id: input.getAttribute('data-variant-id'), quantity: qty });
            totalQty += qty;
          }
        });
        
        if (totalQty === 0) return null;
        return { itemsToAdd: itemsToAdd, totalQty: totalQty };
      }

      function getAllMatrixVariantIds() {
        const activeWrapper = document.querySelector('[data-b2b-matrix]');
        if (!activeWrapper) return [];
        return Array.from(activeWrapper.querySelectorAll('[data-row-input]')).map(el => el.getAttribute('data-variant-id'));
      }

      function buildClonedPayload(matrixData, globoProps, rawAddons) {
        const finalItems = [];
        
        matrixData.itemsToAdd.forEach(mi => {
            const uniqueSuffix = '-' + mi.id;
            
            const mainProps = Object.assign({}, globoProps);
            for (let k in mainProps) {
                if (k.startsWith('_gpo') && typeof mainProps[k] === 'string' && !mainProps[k].includes('/')) {
                    mainProps[k] = mainProps[k] + uniqueSuffix;
                }
            }
            mainProps['_b2b_size'] = mi.id;
            finalItems.push({ id: mi.id, quantity: mi.quantity, properties: mainProps });

            rawAddons.forEach(addon => {
                const addonProps = Object.assign({}, addon.properties || {});
                for (let k in addonProps) {
                    if (k.startsWith('_gpo') && typeof addonProps[k] === 'string' && !addonProps[k].includes('/')) {
                        addonProps[k] = addonProps[k] + uniqueSuffix;
                    }
                }
                addonProps['_b2b_size'] = mi.id;
                finalItems.push({ id: addon.id, quantity: mi.quantity, properties: addonProps });
            });
        });

        return { items: finalItems, _b2bExpanded: true };
      }

      function expandJsonPayload(bodyObj) {
        const matrixData = getMatrixItems();
        if (!matrixData) return bodyObj;

        let itemsArray = [];
        if (bodyObj.items) itemsArray = Array.isArray(bodyObj.items) ? bodyObj.items.slice() : Object.values(bodyObj.items);
        else if (bodyObj.id) itemsArray = [bodyObj];
        if (itemsArray.length === 0) return bodyObj;

        const allVariantIds = getAllMatrixVariantIds();
        const mainIndex = itemsArray.findIndex(it => allVariantIds.includes(String(it.id)));

        if (mainIndex === -1) {
          itemsArray.forEach(item => { item.quantity = matrixData.totalQty; });
          return { items: itemsArray, _b2bExpanded: true };
        }

        const mainItem = itemsArray[mainIndex];
        const globoProps = mainItem.properties || {};

        itemsArray.splice(mainIndex, 1); 
        const originalAddons = itemsArray;

        return buildClonedPayload(matrixData, globoProps, originalAddons);
      }

      const origFetch = window.fetch;
      window.fetch = async function(...args) {
        const [resource, config] = args;
        const url = typeof resource === 'string' ? resource : (resource instanceof Request ? resource.url : '');
        if (url && (url.includes('/cart/add.js') || url.includes('/cart/add'))) {
          if (config && config.body && typeof config.body === 'string') {
            try {
              const bodyObj = JSON.parse(config.body);
              if (!bodyObj._b2bExpanded) config.body = JSON.stringify(expandJsonPayload(bodyObj));
            } catch (e) {}
          }
        }
        return origFetch.apply(this, args);
      };

      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url) {
        this._b2bUrl = url;
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function(body) {
        if (this._b2bUrl && (this._b2bUrl.includes('/cart/add.js') || this._b2bUrl.includes('/cart/add'))) {
          if (body && typeof body === 'string') {
            try {
              const bodyObj = JSON.parse(body);
              if (!bodyObj._b2bExpanded) body = JSON.stringify(expandJsonPayload(bodyObj));
            } catch (e) {}
          } 
          else if (body instanceof FormData) {
            const matrixData = getMatrixItems();
            const allVariantIds = getAllMatrixVariantIds();

            if (matrixData && allVariantIds.length > 0) {
              let containsMain = false;
              let globoProps = {};

              for (let [key, value] of body.entries()) {
                if ((key === 'id' || key.includes('[id]')) && allVariantIds.includes(String(value))) {
                  containsMain = true;
                }
                if (key.startsWith('properties[')) {
                   const pName = key.substring(11, key.length - 1);
                   globoProps[pName] = value;
                }
              }

              if (containsMain) {
                const addonsMap = {};
                for (let [key, value] of body.entries()) {
                   const match = key.match(/items\[(\d+)\]\[(.*?)\]/);
                   if (match) {
                      const idx = match[1];
                      const prop = match[2];
                      if (!addonsMap[idx]) addonsMap[idx] = {};
                      if (prop.startsWith('properties[')) {
                          if (!addonsMap[idx].properties) addonsMap[idx].properties = {};
                          const pName = prop.substring(11, prop.length - 1);
                          addonsMap[idx].properties[pName] = value;
                      } else {
                          addonsMap[idx][prop] = value;
                      }
                   }
                }
                
                const originalAddons = Object.values(addonsMap).filter(addon => addon.id && !allVariantIds.includes(String(addon.id)));
                const newPayload = buildClonedPayload(matrixData, globoProps, originalAddons);
                
                this.setRequestHeader('Content-Type', 'application/json');
                body = JSON.stringify(newPayload);
              } else {
                const qtyKeys = [];
                for (let [key, value] of body.entries()) {
                   if (key === 'quantity' || key.includes('[quantity]')) qtyKeys.push(key);
                }
                if (qtyKeys.length > 0) {
                   qtyKeys.forEach(k => body.set(k, matrixData.totalQty));
                } else {
                   body.append('quantity', matrixData.totalQty);
                }
              }
            }
          }
        }
        return origSend.call(this, body);
      };
    }

    form.addEventListener('submit', function(e) {
      let hasQty = false;
      wrapper.querySelectorAll('[data-row-input]').forEach(function(inp) {
        if (parseInt(inp.value, 10) > 0) hasQty = true;
      });

      if (!hasQty) {
        if (Object.keys(activeBlocks).length > 0) {
          e.preventDefault(); e.stopImmediatePropagation();
          if (validMsg) {
            validMsg.hidden = false;
            clearTimeout(validMsg._t);
            validMsg._t = setTimeout(() => validMsg.hidden = true, 5000);
            validMsg.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
        return;
      }

      if (validMsg) validMsg.hidden = true;

      if (!e.defaultPrevented) {
        e.preventDefault();
        const atcButton = form.querySelector('[type="submit"], button[name="add"]');
        const origLabel = atcButton ? (atcButton.textContent || 'Add to cart') : 'Add to cart';
        if (atcButton) { atcButton.disabled = true; atcButton.textContent = 'Adding...'; }
        const { textProps } = { textProps: collectProperties(form) };
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: form.querySelector('input[name="id"]').value,
            quantity: 1, 
            properties: textProps
          })
        }).then(r => r.json()).then(() => {
          if (atcButton) { atcButton.disabled = false; atcButton.textContent = origLabel; }
          document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          const sc = document.querySelector('.cart-drawer, .mini-cart, [data-cart-drawer], #cart-drawer');
          if (sc) sc.classList.add('is-open', 'active', 'open');
          else window.location.href = '/cart';
        }).catch(err => {
          if (atcButton) { atcButton.disabled = false; atcButton.textContent = origLabel; }
          alert('Error adding to cart.');
        });
      }
    });

  }

  function boot() { document.querySelectorAll('[data-b2b-matrix]').forEach(initMatrix); }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
  document.addEventListener('shopify:section:load', function () { document.querySelectorAll('[data-b2b-matrix]').forEach(initMatrix); });

})();